import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AVAILABLE_VETERINARIANS_QUERY,
  MY_PETS_QUERY,
  REQUEST_APPOINTMENT_MUTATION,
} from './portal.gql';

import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */

const SLOTS: string[] = Array.from({ length: 16 }, (_, i) => {
  const total = 9 * 60 + i * 30;
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
});

function todayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function dateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function errText(e: unknown, t: (k: string) => string): string {
  const raw = e instanceof ApolloError ? (e.graphQLErrors[0]?.message ?? e.message) : e instanceof Error ? e.message : 'error';
  if (raw.includes('pet_already_has_active_appointment')) return t('portal.wizard_error_pet_active');
  if (raw.includes('appointment_outside_business_hours')) return t('portal.wizard_error_hours');
  if (raw.includes('appointment_in_past')) return t('portal.wizard_error_past');
  if (raw.includes('appointment_overlap')) return t('portal.wizard_error_overlap');
  return raw;
}

function MonthGrid({
  selected,
  onSelect,
}: {
  selected: Date | null;
  onSelect: (d: Date) => void;
}): JSX.Element {
  const [cursor, setCursor] = useState(() => {
    const d = todayLocal();
    d.setDate(1);
    return d;
  });
  const today = todayLocal();

  const cells = useMemo(() => {
    const first_of_month = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start_offset = first_of_month.getDay(); // 0=Sunday
    const days_in_month = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr: (Date | null)[] = [];
    for (let i = 0; i < start_offset; i++) arr.push(null);
    for (let day = 1; day <= days_in_month; day++) {
      arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
    }
    return arr;
  }, [cursor]);

  return (
    <div className="bg-background rounded-xl border p-3">
      <div className="mb-2 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <span className="text-sm font-medium">
          {cursor.toLocaleDateString([], { month: 'long', year: 'numeric' })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="text-muted-foreground grid grid-cols-7 gap-1 text-center text-[10px] uppercase">
        {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const disabled = d.getTime() < today.getTime();
          const is_selected = selected && dateKey(selected) === dateKey(d);
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d)}
              className={`aspect-square rounded-md text-sm ${
                is_selected
                  ? 'bg-primary text-primary-foreground'
                  : disabled
                    ? 'text-muted-foreground/40'
                    : 'hover:bg-secondary'
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PortalRequestAppointmentPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: petsData } = useQuery(MY_PETS_QUERY, { fetchPolicy: 'cache-and-network' });
  const pets: any[] = petsData?.myPets ?? [];

  const [pet_id, setPetId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [vet_id, setVetId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const scheduled_start_at = useMemo(() => {
    if (!date || !time) return null;
    return new Date(`${dateKey(date)}T${time}:00`);
  }, [date, time]);

  const { data: vetsData, loading: loadingVets } = useQuery(AVAILABLE_VETERINARIANS_QUERY, {
    variables: { scheduled_start_at: scheduled_start_at?.toISOString() },
    skip: !scheduled_start_at,
    fetchPolicy: 'network-only',
  });
  const available_vets: any[] = vetsData?.availableVeterinarians ?? [];

  const [requestAppointment, { loading: submitting }] = useMutation(REQUEST_APPOINTMENT_MUTATION);

  const active_pet_id = pet_id ?? (pets.length === 1 ? pets[0].id : null);

  const submit = async (): Promise<void> => {
    if (!active_pet_id || !scheduled_start_at || !vet_id) return;
    setError(null);
    try {
      await requestAppointment({
        variables: {
          input: {
            pet_id: active_pet_id,
            veterinarian_id: vet_id,
            scheduled_start_at: scheduled_start_at.toISOString(),
            reason: reason.trim() || undefined,
          },
        },
      });
      setDone(true);
    } catch (e) {
      setError(errText(e, t));
    }
  };

  if (done) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="text-lg font-semibold">{t('portal.wizard_success_title')}</h2>
        <p className="text-muted-foreground text-sm">{t('portal.wizard_success_body')}</p>
        <Button onClick={() => navigate('/portal/appointments')}>{t('portal.wizard_back_to_appointments')}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('portal.book_cta')}</h2>

      {pets.length > 1 ? (
        <div className="space-y-2">
          <Label>{t('portal.wizard_step_pet')}</Label>
          <div className="flex flex-wrap gap-2">
            {pets.map((p) => (
              <Button
                key={p.id}
                type="button"
                size="sm"
                variant={active_pet_id === p.id ? 'default' : 'outline'}
                onClick={() => setPetId(p.id)}
              >
                {p.name}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label>{t('portal.wizard_step_date')}</Label>
        <MonthGrid
          selected={date}
          onSelect={(d) => {
            setDate(d);
            setTime(null);
            setVetId('');
          }}
        />
      </div>

      {date ? (
        <div className="space-y-2">
          <Label>{t('portal.wizard_step_time')}</Label>
          <div className="grid grid-cols-4 gap-2">
            {SLOTS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setTime(s);
                  setVetId('');
                }}
                className={`rounded-md border py-1.5 text-sm ${
                  time === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-secondary'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {scheduled_start_at ? (
        <div className="space-y-1">
          <Label>{t('portal.wizard_step_vet')}</Label>
          <select
            className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm"
            value={vet_id}
            onChange={(e) => setVetId(e.target.value)}
            disabled={loadingVets}
          >
            <option value="">{loadingVets ? t('common.loading') : t('portal.wizard_vet_placeholder')}</option>
            {available_vets.map((v) => (
              <option key={v.id} value={v.id}>
                {v.full_name}
              </option>
            ))}
          </select>
          {!loadingVets && available_vets.length === 0 ? (
            <p className="text-muted-foreground text-xs">{t('portal.wizard_no_vets_available')}</p>
          ) : null}
        </div>
      ) : null}

      {vet_id ? (
        <div className="space-y-1">
          <Label>{t('portal.wizard_step_reason')}</Label>
          <textarea
            className="border-input bg-background min-h-[72px] w-full rounded-md border px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      ) : null}

      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
          {error}
        </div>
      ) : null}

      <Button
        className="w-full"
        disabled={!active_pet_id || !scheduled_start_at || !vet_id || submitting}
        onClick={() => void submit()}
      >
        {submitting ? t('common.loading') : t('portal.wizard_submit')}
      </Button>
    </div>
  );
}
